import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

export interface BomItem {
  id?: string;
  _id?: number; // For tracking bom-extraction-items record ID
  item: string;
  quantity: string | number;
  description: string;
  unit?: string;
  unitCost?: number;
  totalCost?: number;
  partNumber?: string;
  cost?: number;
  vendor?: string;
  notes?: string;
  material?: string;
  dimensions?: string;
  specification?: string;
  confidenceScore?: number;
  requiresReview?: boolean;
  reviewNotes?: string;
  sortOrder?: number;
  linkedProductId?: string;
}

export interface BomExtractionRecord {
  id: string | number;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedAt?: Date;
  uploadedBy?: string;
  extractionStatus: "pending" | "processing" | "completed" | "failed";
  extractedText?: string;
  bomData: string; // JSON string of BomItem[]
  extractedData?: BomItem[]; // Parsed version of bomData for UI
  editedData?: BomItem[];
  blobUrl?: string;
  jobId?: number;
  panelId?: number;
  errorMessage?: string;
  extractedAt?: string;
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

export class BomExtractionViewStore {
  // Upload state
  uploadingFiles: File[] = [];
  uploadProgress: number = 0;
  uploadError: string | null = null;
  isDragging: boolean = false;

  // Demo mode when backend not available
  isDemoMode: boolean = false;
  demoExtractions: BomExtractionRecord[] = [];

  // Document state
  selectedExtractionId: string | number | null = null;
  documentBlobUrl: string | null = null;
  documentZoom: number = 100;
  documentRotation: number = 0;

  // BOM data
  extractedBom: BomItem[] = [];
  editedBom: BomItem[] = [];
  hasUnsavedChanges: boolean = false;
  selectedBomRows: string[] = [];

  // UI state
  viewMode: "grid" | "list" = "grid";
  isProcessing: boolean = false;
  searchQuery: string = "";
  filterStatus: string = "all";
  exportFormat: "csv" | "excel" | "json" = "csv";

  // Viewer state
  viewerSplitPosition: number = 60;
  isFullscreen: boolean = false;
  showAnnotations: boolean = true;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);

    // Check if service store exists, if not enable demo mode
    const bomExtractionStore = (this.rootStore as any).bomExtractionStore;
    if (!bomExtractionStore) {
      console.log("[BOM ViewStore] No service store found, enabling demo mode");
      this.enableDemoMode();
    } else {
      console.log("[BOM ViewStore] Service store found, will attempt real uploads");

      // Monitor for service errors
      if (bomExtractionStore.serviceHasError) {
        console.warn("[BOM ViewStore] Service has existing errors, may fall back to demo mode");
      }
    }
  }

  enableDemoMode() {
    this.isDemoMode = true;
    // Add some demo extractions
    this.demoExtractions = [
      {
        id: "demo-1",
        fileName: "sample-blueprint.pdf",
        fileSize: 2457600,
        mimeType: "application/pdf",
        uploadedAt: new Date("2024-01-15"),
        uploadedBy: "Demo User",
        extractionStatus: "completed",
        bomData: JSON.stringify([
          { item: "A", quantity: 4, description: "Steel Beam 10ft x 6in", unit: "pcs", cost: 150 },
          { item: "B", quantity: 12, description: "Concrete Anchor Bolts", unit: "pcs", cost: 8.50 },
          { item: "C", quantity: 2, description: "Support Brackets", unit: "pcs", cost: 45 },
          { item: "D", quantity: 100, description: "Welding Rods", unit: "pcs", cost: 2.25 },
        ]),
        extractedData: [
          { item: "A", quantity: 4, description: "Steel Beam 10ft x 6in", unit: "pcs", cost: 150 },
          { item: "B", quantity: 12, description: "Concrete Anchor Bolts", unit: "pcs", cost: 8.50 },
          { item: "C", quantity: 2, description: "Support Brackets", unit: "pcs", cost: 45 },
          { item: "D", quantity: 100, description: "Welding Rods", unit: "pcs", cost: 2.25 },
        ],
      },
      {
        id: "demo-2",
        fileName: "electrical-schematic.jpg",
        fileSize: 987654,
        mimeType: "image/jpeg",
        uploadedAt: new Date("2024-01-14"),
        uploadedBy: "Demo User",
        extractionStatus: "completed",
        bomData: JSON.stringify([
          { item: "1", quantity: 50, description: "LED Light Fixtures", unit: "pcs", cost: 32 },
          { item: "2", quantity: 200, description: "14 AWG Wire", unit: "ft", cost: 0.85 },
          { item: "3", quantity: 25, description: "Circuit Breakers 20A", unit: "pcs", cost: 12 },
        ]),
        extractedData: [
          { item: "1", quantity: 50, description: "LED Light Fixtures", unit: "pcs", cost: 32 },
          { item: "2", quantity: 200, description: "14 AWG Wire", unit: "ft", cost: 0.85 },
          { item: "3", quantity: 25, description: "Circuit Breakers 20A", unit: "pcs", cost: 12 },
        ],
      },
      {
        id: "demo-3",
        fileName: "plumbing-diagram.png",
        fileSize: 543210,
        mimeType: "image/png",
        uploadedAt: new Date("2024-01-13"),
        uploadedBy: "Demo User",
        extractionStatus: "failed",
        bomData: "[]",
        extractedData: [],
        errorMessage: "Could not extract BOM data from image"
      }
    ];
  }

  // Actions
  setViewMode(mode: "grid" | "list") {
    this.viewMode = mode;
  }

  setUploadingFiles(files: File[]) {
    this.uploadingFiles = files;
    this.uploadError = null;
  }

  addUploadingFiles(files: File[]) {
    const validFiles = files.filter((file) => {
      const isValid = this.validateFile(file);
      if (!isValid) {
        this.setUploadError(`Invalid file: ${file.name}`);
      }
      return isValid;
    });
    this.uploadingFiles = [...this.uploadingFiles, ...validFiles];
  }

  removeUploadingFile(index: number) {
    this.uploadingFiles = this.uploadingFiles.filter((_, i) => i !== index);
  }

  clearUploadingFiles() {
    this.uploadingFiles = [];
    this.uploadProgress = 0;
    this.uploadError = null;
  }

  setUploadProgress(progress: number) {
    this.uploadProgress = progress;
  }

  setUploadError(error: string | null) {
    this.uploadError = error;
  }

  setIsDragging(dragging: boolean) {
    this.isDragging = dragging;
  }

  setSelectedExtractionId(id: string | null) {
    this.selectedExtractionId = id;
    if (id) {
      this.loadExtractionData(id);
    }
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  setFilterStatus(status: string) {
    this.filterStatus = status;
  }

  setExportFormat(format: "csv" | "excel" | "json") {
    this.exportFormat = format;
  }

  updateBomItem(index: number, field: keyof BomItem, value: any) {
    if (!this.editedBom[index]) {
      return;
    }
    this.editedBom[index] = {
      ...this.editedBom[index],
      [field]: value,
    };
    this.hasUnsavedChanges = true;
  }

  addBomItem() {
    const newItem: BomItem = {
      item: "",
      quantity: 1,
      description: "",
    };
    this.editedBom = [...this.editedBom, newItem];
    this.hasUnsavedChanges = true;
  }

  removeBomItem(index: number) {
    this.editedBom = this.editedBom.filter((_, i) => i !== index);
    this.hasUnsavedChanges = true;
  }

  setSelectedBomRows(rows: string[]) {
    this.selectedBomRows = rows;
  }

  setDocumentZoom(zoom: number) {
    this.documentZoom = Math.max(50, Math.min(200, zoom));
  }

  setDocumentRotation(rotation: number) {
    this.documentRotation = rotation % 360;
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
  }

  setViewerSplitPosition(position: number) {
    this.viewerSplitPosition = position;
  }

  toggleAnnotations() {
    this.showAnnotations = !this.showAnnotations;
  }

  // Async actions
  async checkServiceAvailability(): Promise<boolean> {
    try {
      const bomExtractionStore = (this.rootStore as any).bomExtractionStore as ServiceStore;
      if (!bomExtractionStore) {
        console.log("[BOM Upload] Service store not available");
        return false;
      }

      // Try a simple find operation to test if service is available
      console.log("[BOM Upload] Checking service availability...");
      await bomExtractionStore.find({ query: { $limit: 1 } });
      console.log("[BOM Upload] Service is available");
      return true;
    } catch (error: unknown) {
      const err = error as { code?: number; message?: string };
      console.log("[BOM Upload] Service check failed:", err.message);
      if (err.code === 404 || err.message?.includes('Service') || err.message?.includes('No record found')) {
        console.log("[BOM Upload] Service not configured on backend");
        return false;
      }
      // Check for validation errors which mean the service exists
      if (err.message?.includes('validation')) {
        console.log("[BOM Upload] Service exists but has validation requirements");
        return true;
      }
      // For other errors, still return true (service exists but may have other issues)
      return true;
    }
  }

  async uploadAndExtract() {
    console.log("[BOM Upload] Starting upload process");
    if (this.uploadingFiles.length === 0) {
      throw new Error("No files to upload");
    }

    // Check file sizes before uploading
    // Reduce limit to 5MB to account for base64 encoding overhead (increases size by ~33%)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit (becomes ~6.6MB base64)
    for (const file of this.uploadingFiles) {
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        throw new Error(`File "${file.name}" is too large (${sizeMB}MB). Maximum size is 5MB to ensure reliable WebSocket transport.`);
      }
    }

    this.isProcessing = true;
    this.uploadError = null;

    // Pre-flight check for service availability
    if (!this.isDemoMode) {
      const isAvailable = await this.checkServiceAvailability();
      if (!isAvailable) {
        console.log("[BOM Upload] Service not available, switching to demo mode");
        runInAction(() => {
          this.enableDemoMode();
        });
      }
    }

    try {
      // Handle demo mode
      if (this.isDemoMode) {
        // Simulate upload progress
        for (let i = 0; i < this.uploadingFiles.length; i++) {
          const file = this.uploadingFiles[i];

          // Simulate processing time
          await new Promise(resolve => setTimeout(resolve, 1000));

          runInAction(() => {
            this.uploadProgress = ((i + 1) / this.uploadingFiles.length) * 100;
          });

          // Create a demo extraction
          const extractedItems = [
            { item: "1", quantity: 10, description: "Sample Item from " + file.name, unit: "pcs", cost: 25 },
            { item: "2", quantity: 5, description: "Another Component", unit: "pcs", cost: 50 },
            { item: "3", quantity: 20, description: "Hardware Kit", unit: "set", cost: 15 },
          ];
          const newExtraction: BomExtractionRecord = {
            id: `demo-${Date.now()}-${i}`,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            uploadedAt: new Date(),
            uploadedBy: "Demo User",
            extractionStatus: "completed",
            bomData: JSON.stringify(extractedItems),
            extractedData: extractedItems,
          };

          runInAction(() => {
            this.demoExtractions = [newExtraction, ...this.demoExtractions];
            this.selectedExtractionId = newExtraction.id;
            this.extractedBom = newExtraction.extractedData;
            this.editedBom = [...newExtraction.extractedData];
          });
        }

        runInAction(() => {
          this.clearUploadingFiles();
          this.isProcessing = false;
        });

        return;
      }

      console.log("[BOM Upload] Not in demo mode, attempting real upload via Azure Blob Storage...");

      for (let i = 0; i < this.uploadingFiles.length; i++) {
        const file = this.uploadingFiles[i];
        console.log(`[BOM Upload] Processing file ${i + 1}/${this.uploadingFiles.length}: ${file.name} (${(file.size / 1024).toFixed(2)}KB)`);

        try {

          // Upload and extract
          const bomExtractionStore = (this.rootStore as any).bomExtractionStore as ServiceStore;
          if (!bomExtractionStore) {
            throw new Error("BOM extraction service is not available");
          }

          console.log("[BOM Upload] Starting Azure Blob Storage upload process...");

          let result;
          try {
            // Step 1: Request a SAS URL from the backend (following PdfManagementStore pattern)
            console.log("[BOM Upload] Step 1/4: Requesting SAS URL for direct Azure upload...");

            const uploadData = {
              requestBlobUrl: true, // Request SAS URL for direct upload
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type,
              bomData: "[]", // Required field - start empty
              extractionStatus: "pending",
              metadata: {
                uploadedBy: (this.rootStore as any).authStore?.user?.email || "unknown",
                uploadedAt: new Date().toISOString(),
              },
            };

            result = await bomExtractionStore.create(uploadData);

            console.log("[BOM Upload] Got response from backend:", result);
            console.log("[BOM Upload] Response keys:", Object.keys(result || {}));

            // Check if uploadUrl is nested in bomData object (backend nesting issue)
            let uploadUrl = result?.uploadUrl;
            if (!uploadUrl && result?.bomData && typeof result.bomData === 'object') {
              uploadUrl = result.bomData.uploadUrl;
              console.log("[BOM Upload] Found uploadUrl nested in bomData:", uploadUrl);
            }
            console.log("[BOM Upload] uploadUrl value:", uploadUrl);

            // Step 2: If we got a SAS URL (uploadUrl field), upload directly to Azure
            if (result && uploadUrl) {
              console.log("[BOM Upload] Step 2/4: Got SAS URL, uploading directly to Azure Blob Storage...");

              runInAction(() => {
                this.uploadProgress = 30;
              });

              // Upload directly to Azure using HTTP PUT (like PdfManagementStore)
              const azureResponse = await fetch(uploadUrl, {
                method: "PUT",
                headers: {
                  "x-ms-blob-type": "BlockBlob",
                  "Content-Type": file.type || "application/pdf",
                  // Don't set Content-Length - browser handles it
                },
                body: file, // Send raw file, not Base64
              });

              if (!azureResponse.ok) {
                throw new Error(
                  `Azure upload failed: ${azureResponse.status} ${azureResponse.statusText}`
                );
              }

              console.log("[BOM Upload] Step 3/4: File uploaded successfully to Azure!");
              runInAction(() => {
                this.uploadProgress = 60;
              });

              // Step 4: Trigger backend processing with processFromBlob flag
              // The backend will retrieve the PDF from Azure and process it
              if (result.id) {
                try {
                  console.log("[BOM Upload] Step 4/4: Triggering backend processing with processFromBlob...");
                  const processResult = await bomExtractionStore.patch(result.id, {
                    processFromBlob: true,  // This triggers the backend to process the uploaded file
                  });
                  console.log("[BOM Upload] Backend processing triggered successfully:", processResult);
                  result = processResult || result;

                  // The backend will now:
                  // 1. Retrieve the PDF from Azure Blob Storage
                  // 2. Process it with Azure Vision OCR
                  // 3. Extract BOM with Azure OpenAI
                  // 4. Update the record with results
                } catch (patchError) {
                  console.warn("[BOM Upload] Could not trigger processing, backend may process automatically:", patchError);
                }
              }

              runInAction(() => {
                this.uploadProgress = 80;
              });

              // All steps completed successfully
              console.log("[BOM Upload] All 4 steps completed! Backend is processing with Azure Vision OCR.");

            } else {
              // Fallback: If no SAS URL provided, the backend might not support direct upload yet
              console.log("[BOM Upload] No SAS URL provided. Backend may need to implement uploadUrl support.");
              console.log("[BOM Upload] Record created:", result);

              // For now, we can't upload the file without a SAS URL
              // The backend needs to implement the uploadUrl field like pdf-documents service
              console.warn("[BOM Upload] Cannot upload file without SAS URL. Backend needs to provide uploadUrl field.");
            }

            // Parse any returned BOM data
            if (result && result.bomData) {
              // Check if bomData is a nested object (backend nesting issue) or a string
              if (typeof result.bomData === 'object' && result.bomData.bomData) {
                // Nested structure - extract the actual bomData string
                const actualBomData = result.bomData.bomData;
                if (actualBomData && actualBomData !== "[]") {
                  try {
                    result.extractedData = JSON.parse(actualBomData);
                    console.log("[BOM Upload] Backend returned extracted data:", result.extractedData);
                  } catch (e) {
                    console.log("[BOM Upload] Could not parse nested bomData");
                  }
                }
              } else if (typeof result.bomData === 'string' && result.bomData !== "[]") {
                // Normal string bomData
                try {
                  result.extractedData = JSON.parse(result.bomData);
                  console.log("[BOM Upload] Backend returned extracted data:", result.extractedData);
                } catch (e) {
                  console.log("[BOM Upload] Could not parse bomData string");
                }
              }
            }
          } catch (createError: any) {
            console.error("[BOM Upload] Service error:", createError);

            // Check if it's a service not found error
            if (createError.message?.includes('No record found') ||
                createError.message?.includes('Service') ||
                createError.message?.includes('validation') ||
                createError.code === 404) {
              console.warn("[BOM Upload] Service not available or not configured, falling back to demo mode");

              // Enable demo mode and retry
              runInAction(() => {
                this.enableDemoMode();
              });

              // Create demo extraction
              const demoItems = [
                { item: "DEMO-1", quantity: 10, description: "Demo extraction from " + file.name, unit: "pcs", cost: 25 },
                { item: "DEMO-2", quantity: 5, description: "Sample Component", unit: "pcs", cost: 50 },
                { item: "DEMO-3", quantity: 20, description: "Hardware Kit", unit: "set", cost: 15 },
              ];
              const demoExtraction: BomExtractionRecord = {
                id: `demo-${Date.now()}`,
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type,
                uploadedAt: new Date(),
                uploadedBy: "Demo User",
                extractionStatus: "completed",
                bomData: JSON.stringify(demoItems),
                extractedData: demoItems,
              };

              runInAction(() => {
                this.demoExtractions = [demoExtraction, ...this.demoExtractions];
                result = demoExtraction;
              });
            } else {
              // Re-throw if it's a different error
              throw createError;
            }
          }

          console.log("[BOM Upload] Final result:", result);

          runInAction(() => {
            this.uploadProgress = ((i + 1) / this.uploadingFiles.length) * 100;
          });

          // If successful, switch to viewer
          if (result && result.id) {
            runInAction(() => {
              this.selectedExtractionId = String(result.id);
            });
          }
        } catch (fileError) {
          console.error(`[BOM Upload] Error uploading file ${file.name}:`, fileError);
          throw new Error(`Failed to upload ${file.name}: ${fileError instanceof Error ? fileError.message : 'Unknown error'}`);
        }
      }

      runInAction(() => {
        this.clearUploadingFiles();
        this.isProcessing = false;
      });
    } catch (error) {
      console.error("[BOM Upload] Upload failed:", error);
      runInAction(() => {
        this.uploadError = error instanceof Error ? error.message : "Upload failed";
        this.isProcessing = false;
      });
      throw error;
    }
  }

  async loadExtractionData(id: string) {
    try {
      // Handle demo mode
      if (this.isDemoMode) {
        const extraction = this.demoExtractions.find(e => e.id === id);
        if (extraction) {
          runInAction(() => {
            this.extractedBom = extraction.extractedData || [];
            this.editedBom = [...this.extractedBom];
            this.documentBlobUrl = null; // No actual blob in demo mode
            this.hasUnsavedChanges = false;
          });
        }
        return;
      }

      const bomExtractionStore = (this.rootStore as any).bomExtractionStore as ServiceStore;
      if (!bomExtractionStore) {
        console.warn("BOM extraction service not available");
        return;
      }

      // Get the extraction record
      const extraction = await bomExtractionStore.get(parseInt(id));
      console.log("[BOM ViewStore] Loaded extraction:", extraction);

      // Load BOM items from the new bom-extraction-items service
      const bomExtractionItemsStore = (this.rootStore as any).bomExtractionItemsStore as ServiceStore;
      if (!bomExtractionItemsStore) {
        console.warn("BOM extraction items service not available");
        // Fall back to parsing bomData from extraction if items service not available
        runInAction(() => {
          let extractedData: BomItem[] = [];
          if (extraction && extraction.bomData) {
            // Try to parse bomData as before (fallback)
            if (typeof extraction.bomData === 'string') {
              try {
                extractedData = JSON.parse(extraction.bomData);
              } catch (e) {
                console.error("Failed to parse bomData string:", e);
                extractedData = [];
              }
            } else if (Array.isArray(extraction.bomData)) {
              extractedData = extraction.bomData;
            }
          }
          this.extractedBom = extractedData;
          this.editedBom = [...extractedData];
          this.documentBlobUrl = (extraction && extraction.blobUrl as string) || null;
          this.hasUnsavedChanges = false;
        });
        return;
      }

      // Load items from bom-extraction-items service
      try {
        await bomExtractionItemsStore.find({
          query: {
            extractionId: parseInt(id)
            // Remove $sort as it causes validation errors on backend
          }
        });
      } catch (findError: any) {
        // Ignore "next is not a function" error from backend hook
        // The data still loads correctly despite this error
        if (!findError.message?.includes('next is not a function') && !findError.message?.includes('validation failed')) {
          console.error('[BOM ViewStore] Error loading items:', findError);
        }
      }

      runInAction(() => {
        // Get the items from the store
        const items = bomExtractionItemsStore.entities.filter((item: any) =>
          item.extractionId === parseInt(id)
        );

        console.log("[BOM ViewStore] Loaded BOM items from service:", items);

        // Transform items to BomItem format
        const extractedData: BomItem[] = items.map((item: any) => ({
          item: item.itemCode || item.extractedItem || "",
          quantity: item.quantity || parseFloat(item.extractedQuantity) || 0,
          description: item.description || item.extractedDescription || "",
          unit: item.unit || "EA",
          unitCost: item.unitCost || 0,
          totalCost: (item.quantity || 0) * (item.unitCost || 0),
          vendor: item.vendor,
          partNumber: item.partNumber,
          material: item.material,
          dimensions: item.dimensions,
          specification: item.specification,
          confidenceScore: item.confidenceScore,
          requiresReview: item.requiresReview,
          reviewNotes: item.reviewNotes,
          sortOrder: item.sortOrder,
          _id: item.id // Keep reference to the actual item ID
        }));

        // Sort the items first by sortOrder (if available), then by Item field alphabetically
        extractedData.sort((a, b) => {
          // If both have sortOrder, use that
          if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
            return a.sortOrder - b.sortOrder;
          }
          // Otherwise sort alphabetically by item field (A, B, C, D, E)
          const itemA = (a.item || '').toUpperCase();
          const itemB = (b.item || '').toUpperCase();
          if (itemA < itemB) return -1;
          if (itemA > itemB) return 1;
          return 0;
        });

        console.log("[BOM ViewStore] Transformed and sorted BOM items:", extractedData);
        this.extractedBom = extractedData;
        this.editedBom = [...extractedData];
        this.documentBlobUrl = (extraction && extraction.blobUrl as string) || null;
        this.hasUnsavedChanges = false;
        console.log("[BOM ViewStore] Set editedBom to:", this.editedBom);
      });
    } catch (error) {
      console.error("Error loading extraction data:", error);
    }
  }

  async saveBomChanges() {
    if (!this.selectedExtractionId || !this.hasUnsavedChanges) {
      return;
    }

    try {
      // Handle demo mode
      if (this.isDemoMode) {
        const extraction = this.demoExtractions.find(e => e.id === this.selectedExtractionId);
        if (extraction) {
          extraction.editedData = [...this.editedBom];
        }
        runInAction(() => {
          this.hasUnsavedChanges = false;
        });
        return;
      }

      const bomExtractionItemsStore = (this.rootStore as any).bomExtractionItemsStore as ServiceStore;
      if (!bomExtractionItemsStore) {
        // Fall back to saving to extraction record
        const bomExtractionStore = (this.rootStore as any).bomExtractionStore as ServiceStore;
        if (!bomExtractionStore) {
          throw new Error("BOM extraction service is not available");
        }
        await bomExtractionStore.patch(typeof this.selectedExtractionId === 'string' ? parseInt(this.selectedExtractionId) : this.selectedExtractionId, {
          bomData: JSON.stringify(this.editedBom),
        });
        runInAction(() => {
          this.hasUnsavedChanges = false;
        });
        return;
      }

      // Update each item in bom-extraction-items service
      for (const item of this.editedBom) {
        if (item._id) {
          // Update existing item
          await bomExtractionItemsStore.patch(item._id, {
            itemCode: item.item,
            quantity: item.quantity,
            description: item.description,
            unit: item.unit,
            unitCost: item.unitCost,
            vendor: item.vendor,
            partNumber: item.partNumber,
            material: item.material,
            dimensions: item.dimensions,
            specification: item.specification,
            reviewNotes: item.reviewNotes,
            requiresReview: item.requiresReview || false,
          });
        } else {
          // Create new item
          await bomExtractionItemsStore.create({
            extractionId: typeof this.selectedExtractionId === 'string' ? parseInt(this.selectedExtractionId) : this.selectedExtractionId,
            itemCode: item.item,
            name: item.item,
            quantity: item.quantity,
            description: item.description,
            unit: item.unit,
            unitCost: item.unitCost,
            vendor: item.vendor,
            partNumber: item.partNumber,
            material: item.material,
            dimensions: item.dimensions,
            specification: item.specification,
            status: "pending",
            requiresReview: item.requiresReview || false,
            sortOrder: this.editedBom.indexOf(item),
          });
        }
      }

      runInAction(() => {
        this.hasUnsavedChanges = false;
      });
    } catch (error) {
      console.error("Error saving BOM changes:", error);
      throw error;
    }
  }

  async exportBom() {
    if (this.editedBom.length === 0) {
      throw new Error("No BOM data to export");
    }

    const data = this.editedBom;
    const fileName = `bom-export-${new Date().getTime()}`;

    switch (this.exportFormat) {
      case "csv":
        this.exportToCsv(data, fileName);
        break;
      case "json":
        this.exportToJson(data, fileName);
        break;
      case "excel":
        // Would need xlsx library
        console.warn("Excel export not yet implemented");
        break;
    }
  }

  async reprocessExtraction(id: string) {
    this.isProcessing = true;

    try {
      // Handle demo mode
      if (this.isDemoMode) {
        const extraction = this.demoExtractions.find(e => e.id === id);
        if (extraction) {
          extraction.extractionStatus = "processing";
          // Simulate processing time
          setTimeout(() => {
            runInAction(() => {
              extraction.extractionStatus = "completed";
              extraction.extractedData = [
                { item: "RE-1", quantity: 10, description: "Reprocessed Item", unit: "pcs", cost: 30 },
                { item: "RE-2", quantity: 5, description: "Updated Component", unit: "pcs", cost: 60 },
              ];
              this.extractedBom = extraction.extractedData;
              this.editedBom = [...extraction.extractedData];
            });
          }, 2000);
        }
        runInAction(() => {
          this.isProcessing = false;
        });
        return;
      }

      // Call reprocess endpoint
      const bomExtractionStore = (this.rootStore as any).bomExtractionStore as ServiceStore;
      if (!bomExtractionStore) {
        console.log("[BOM Reprocess] Service not available, switching to demo mode");
        runInAction(() => {
          this.enableDemoMode();
          this.isProcessing = false;
        });
        return;
      }

      try {
        await bomExtractionStore.patch(parseInt(id), {
          extractionStatus: "pending", // Changed from processingStatus to match API
        });
      } catch (patchError: any) {
        console.error("[BOM Reprocess] Patch failed:", patchError);
        // If service not found, switch to demo mode
        if (patchError.code === 404 || patchError.message?.includes('Service')) {
          console.log("[BOM Reprocess] Service not available, switching to demo mode");
          runInAction(() => {
            this.enableDemoMode();
            this.isProcessing = false;
          });
          return;
        }
        throw patchError;
      }

      // Reload the extraction
      await this.loadExtractionData(id);

      runInAction(() => {
        this.isProcessing = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isProcessing = false;
      });
      throw error;
    }
  }

  // Computed values
  get selectedExtraction() {
    if (!this.selectedExtractionId) {
      return null;
    }

    // Use demo data if in demo mode
    if (this.isDemoMode) {
      return this.demoExtractions.find(
        (e) => e.id === this.selectedExtractionId
      );
    }

    const bomExtractionStore = (this.rootStore as any).bomExtractionStore as ServiceStore;
    if (!bomExtractionStore) {
      return null;
    }
    return bomExtractionStore.entities.find(
      (e: any) => e.id === this.selectedExtractionId
    );
  }

  get filteredExtractions() {
    // Use demo data if in demo mode
    if (this.isDemoMode) {
      let extractions = this.demoExtractions;

      // Filter by status
      if (this.filterStatus !== "all") {
        extractions = extractions.filter(
          (e: any) => e.extractionStatus === this.filterStatus
        );
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        extractions = extractions.filter(
          (e: any) =>
            e.fileName?.toLowerCase().includes(query) ||
            e.metadata?.markNumber?.toLowerCase().includes(query) ||
            e.metadata?.projectId?.toLowerCase().includes(query)
        );
      }

      return extractions;
    }

    const bomExtractionStore = (this.rootStore as any).bomExtractionStore as ServiceStore;
    if (!bomExtractionStore) {
      return [];
    }
    let extractions = bomExtractionStore.entities;

    // Filter by status
    if (this.filterStatus !== "all") {
      extractions = extractions.filter(
        (e: any) => e.extractionStatus === this.filterStatus
      );
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      extractions = extractions.filter(
        (e: any) =>
          e.fileName?.toLowerCase().includes(query) ||
          e.metadata?.markNumber?.toLowerCase().includes(query) ||
          e.metadata?.projectId?.toLowerCase().includes(query)
      );
    }

    return extractions;
  }

  get totalBomCost() {
    return this.editedBom.reduce((sum, item) => {
      const quantity = typeof item.quantity === "string"
        ? parseFloat(item.quantity) || 0
        : item.quantity;
      const cost = item.cost || 0;
      return sum + (quantity * cost);
    }, 0);
  }

  get isLoading() {
    const bomExtractionStore = (this.rootStore as any).bomExtractionStore as ServiceStore;
    if (!bomExtractionStore) {
      return false;
    }
    // Only show loading if we don't have entities yet
    // isFindPending can get stuck, so we check if we already have data
    if (bomExtractionStore.entities && bomExtractionStore.entities.length > 0) {
      return false;
    }
    // If in demo mode and we have demo data, don't show loading
    if (this.isDemoMode && this.demoExtractions.length > 0) {
      return false;
    }
    // Show loading only for get operations or if we're truly waiting for initial data
    return bomExtractionStore.isGetPending ||
           (bomExtractionStore.isFindPending && bomExtractionStore.entities.length === 0);
  }

  // Helper methods
  private validateFile(file: File): boolean {
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSize = 5 * 1024 * 1024; // 5MB limit for WebSocket transport
    const minSize = 1024; // 1KB

    if (!allowedTypes.includes(file.type)) {
      return false;
    }
    if (file.size < minSize || file.size > maxSize) {
      return false;
    }
    return true;
  }

  private exportToCsv(data: BomItem[], fileName: string) {
    const headers = ["Item", "Quantity", "Description", "Unit", "Part Number", "Cost", "Vendor", "Notes"];
    const csvContent = [
      headers.join(","),
      ...data.map((item) =>
        [
          item.item,
          item.quantity,
          `"${item.description}"`,
          item.unit || "",
          item.partNumber || "",
          item.cost || "",
          item.vendor || "",
          `"${item.notes || ""}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private exportToJson(data: BomItem[], fileName: string) {
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  cleanup() {
    this.clearUploadingFiles();
    this.selectedExtractionId = null;
    this.documentBlobUrl = null;
    this.extractedBom = [];
    this.editedBom = [];
    this.hasUnsavedChanges = false;
    this.searchQuery = "";
    this.filterStatus = "all";
  }
}

export default BomExtractionViewStore;